import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Image,
  Plus,
  Trash2,
  Edit,
  GripVertical,
  ExternalLink,
  Loader2,
  Upload,
  Eye,
  EyeOff,
  Calendar,
  X,
  ImageOff
} from 'lucide-react';

// Use same-origin API path
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Helper to construct full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  return url;
};

// Normalize link URLs - add https:// if no protocol specified
const normalizeUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return url;
  return `https://${url}`;
};

// Safe Banner Thumbnail component with fallback
const BannerThumbnail = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError || !src) {
    return (
      <div className="w-full h-full flex items-center justify-center opacity-30">
        <ImageOff className="w-6 h-6" />
      </div>
    );
  }
  
  return (
    <img 
      src={getImageUrl(src)} 
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const B2BBannerManager = ({ theme }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const fileInputRef = useRef(null);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Global settings
  const [globalSettings, setGlobalSettings] = useState({
    global_transition: 'slide',
    auto_rotate_interval: 5000
  });
  
  // Form data
  const [formData, setFormData] = useState({
    image_url: '',
    mobile_image_url: '',
    link_url: '',
    alt_text: '',
    is_active: true,
    display_order: 0,
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchBanners();
    fetchGlobalSettings();
  }, []);

  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/banners/settings`, { 
        withCredentials: true 
      });
      setGlobalSettings(res.data);
    } catch (err) {
      console.error('Error fetching banner settings:', err);
    }
  };

  const saveGlobalSettings = async (newSettings) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_BASE}/api/client-admin/banners/settings`, newSettings, { 
        withCredentials: true 
      });
      setGlobalSettings(newSettings);
      toast.success('Settings saved');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/banners`, { 
        withCredentials: true
      });
      setBanners(res.data.banners || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
      if (err.response?.status === 403) {
        // Feature not enabled - don't show error
      } else if (err.response?.status === 401) {
        // Session expired - suggest refresh
        toast.error('Session expired. Please refresh the page and log in again.');
      } else {
        toast.error('Failed to load banners');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, or WebP image');
      return;
    }

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Image must be less than 25MB`);
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_BASE}/api/client-admin/banners/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        setFormData({ ...formData, image_url: res.data.url });
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Upload failed - no URL returned');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url || !formData.link_url) {
      toast.error('Image and Link URL are required');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_BASE}/api/client-admin/banners/${editingId}`, formData, { 
          withCredentials: true
        });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API_BASE}/api/client-admin/banners`, {
          ...formData,
          display_order: banners.length
        }, { 
          withCredentials: true
        });
        toast.success('Banner created');
      }
      
      resetForm();
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!confirm('Are you sure you want to delete this banner?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/banners/${bannerId}`, { 
        withCredentials: true
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await axios.put(`${API_BASE}/api/client-admin/banners/${banner.id}`, {
        is_active: !banner.is_active
      }, { 
        withCredentials: true
      });
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      fetchBanners();
    } catch (err) {
      toast.error('Failed to update banner');
    }
  };

  const handleEdit = (banner) => {
    setFormData({
      image_url: banner.image_url || '',
      mobile_image_url: banner.mobile_image_url || '',
      link_url: banner.link_url || '',
      alt_text: banner.alt_text || '',
      is_active: banner.is_active,
      display_order: banner.display_order || 0,
      start_date: banner.start_date || '',
      end_date: banner.end_date || ''
    });
    setEditingId(banner.id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      image_url: '',
      mobile_image_url: '',
      link_url: '',
      alt_text: '',
      is_active: true,
      display_order: 0,
      start_date: '',
      end_date: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newBanners = [...banners];
    const [draggedBanner] = newBanners.splice(draggedIndex, 1);
    newBanners.splice(dropIndex, 0, draggedBanner);
    
    setBanners(newBanners);
    
    try {
      const bannerIds = newBanners.map(b => b.id);
      await axios.post(`${API_BASE}/api/client-admin/banners/reorder`, 
        { banner_ids: bannerIds },
        { withCredentials: true }
      );
      toast.success('Banner order updated');
    } catch (err) {
      console.error('Reorder error:', err);
      toast.error('Failed to update order');
      fetchBanners();
    }
  };

  const activeCount = banners.filter(b => b.is_active).length;

  // Use theme colors or defaults
  const primaryColor = theme?.primary_color || theme?.brand_color || '#3B82F6';
  const bgSecondary = theme?.background_secondary || theme?.card_color || '#1E293B';
  const textPrimary = theme?.text_primary || theme?.text_color || '#F8FAFC';
  const textSecondary = theme?.text_secondary || '#94A3B8';
  const borderColor = theme?.border_color || '#334155';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  return (
    <div 
      className="rounded-xl p-4"
      style={{ 
        backgroundColor: bgSecondary,
        border: `1px solid ${borderColor}`
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: textPrimary }}>
          <Image className="w-5 h-5" style={{ color: primaryColor }} />
          Banner Management
          <span className="text-xs font-normal ml-2" style={{ color: textSecondary }}>
            ({activeCount} active)
          </span>
        </h2>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          size="sm"
          style={{ backgroundColor: primaryColor, color: theme?.text_on_brand || '#ffffff' }}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Banner
        </Button>
      </div>
      
      {/* Global Settings */}
      <div 
        className="mb-4 p-3 rounded-lg"
        style={{ 
          backgroundColor: `${borderColor}30`,
          border: `1px solid ${borderColor}50`
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-xs block mb-1" style={{ color: textSecondary }}>Transition Effect</label>
              <select
                value={globalSettings.global_transition}
                onChange={(e) => saveGlobalSettings({ ...globalSettings, global_transition: e.target.value })}
                disabled={savingSettings}
                className="text-sm px-3 py-1.5 rounded-lg focus:outline-none"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              >
                <option value="slide">Slide</option>
                <option value="fade">Fade</option>
                <option value="zoom">Zoom</option>
                <option value="flip">Flip</option>
                <option value="none">None (Instant)</option>
              </select>
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: textSecondary }}>Auto-Rotate Interval</label>
              <select
                value={globalSettings.auto_rotate_interval}
                onChange={(e) => saveGlobalSettings({ ...globalSettings, auto_rotate_interval: parseInt(e.target.value) })}
                disabled={savingSettings}
                className="text-sm px-3 py-1.5 rounded-lg focus:outline-none"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              >
                <option value={3000}>3 seconds</option>
                <option value={5000}>5 seconds</option>
                <option value={7000}>7 seconds</option>
                <option value={10000}>10 seconds</option>
                <option value={15000}>15 seconds</option>
              </select>
            </div>
          </div>
          {savingSettings && <span className="text-xs" style={{ color: primaryColor }}>Saving...</span>}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <form 
          onSubmit={handleSubmit} 
          className="mb-4 p-4 rounded-lg"
          style={{ 
            backgroundColor: `${borderColor}30`,
            border: `1px solid ${borderColor}50`
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold" style={{ color: textPrimary }}>
              {editingId ? 'Edit Banner' : 'Add New Banner'}
            </h3>
            <button type="button" onClick={resetForm} className="p-1 hover:opacity-70">
              <X className="w-4 h-4" style={{ color: textSecondary }} />
            </button>
          </div>
          
          <div className="grid gap-3">
            {/* Image Upload */}
            <div>
              <label className="text-xs mb-1 block" style={{ color: textSecondary }}>Banner Image *</label>
              
              <div className="flex gap-2 mb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="b2b-banner-upload"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1"
                  style={{ 
                    backgroundColor: bgSecondary, 
                    border: `1px solid ${primaryColor}50`,
                    color: textPrimary
                  }}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </>
                  )}
                </Button>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-px" style={{ backgroundColor: `${borderColor}50` }} />
                <span className="text-xs" style={{ color: textSecondary }}>or paste URL</span>
                <div className="flex-1 h-px" style={{ backgroundColor: `${borderColor}50` }} />
              </div>
              
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/banner.jpg"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              />
              <p className="text-xs mt-1" style={{ color: textSecondary }}>Recommended: 1200x400px (Desktop)</p>
              
              {/* Image Preview */}
              {formData.image_url && (
                <div className="mt-2 rounded-lg overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
                  <img 
                    src={getImageUrl(formData.image_url)} 
                    alt="Preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
            
            {/* Mobile Image */}
            <div>
              <label className="text-xs mb-1 block" style={{ color: textSecondary }}>Mobile Image (Optional)</label>
              <Input
                value={formData.mobile_image_url}
                onChange={(e) => setFormData({ ...formData, mobile_image_url: e.target.value })}
                placeholder="https://example.com/banner-mobile.jpg"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              />
              <p className="text-xs mt-1" style={{ color: textSecondary }}>Shown on phones. If empty, desktop image is used.</p>
            </div>
            
            {/* Link URL */}
            <div>
              <label className="text-xs mb-1 block" style={{ color: textSecondary }}>Link URL * (where banner links to)</label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://example.com/promo"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              />
            </div>
            
            {/* Alt Text */}
            <div>
              <label className="text-xs mb-1 block" style={{ color: textSecondary }}>Alt Text (for accessibility)</label>
              <Input
                value={formData.alt_text}
                onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                placeholder="Banner description"
                style={{ 
                  backgroundColor: bgSecondary, 
                  border: `1px solid ${borderColor}`,
                  color: textPrimary
                }}
              />
            </div>
            
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs mb-1 flex items-center gap-1" style={{ color: textSecondary }}>
                  <Calendar className="w-3 h-3" /> Start Date
                </label>
                <Input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  style={{ 
                    backgroundColor: bgSecondary, 
                    border: `1px solid ${borderColor}`,
                    color: textPrimary
                  }}
                />
              </div>
              <div>
                <label className="text-xs mb-1 flex items-center gap-1" style={{ color: textSecondary }}>
                  <Calendar className="w-3 h-3" /> End Date
                </label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  style={{ 
                    backgroundColor: bgSecondary, 
                    border: `1px solid ${borderColor}`,
                    color: textPrimary
                  }}
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={resetForm}
                className="flex-1"
                style={{ 
                  border: `1px solid ${borderColor}`,
                  color: textSecondary
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="flex-1"
                style={{ backgroundColor: primaryColor, color: theme?.text_on_brand || '#ffffff' }}
              >
                {editingId ? 'Update Banner' : 'Create Banner'}
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Banners List */}
      {banners.length === 0 ? (
        <div className="text-center py-8">
          <Image className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: textSecondary }} />
          <p style={{ color: textSecondary }}>No banners yet</p>
          <p className="text-sm mt-1" style={{ color: textSecondary }}>Click "Add Banner" to create your first banner</p>
        </div>
      ) : (
        <div className="space-y-2">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-move transition-all ${
                dragOverIndex === index ? 'ring-2' : ''
              }`}
              style={{ 
                backgroundColor: bgSecondary,
                border: `1px solid ${borderColor}`,
                opacity: banner.is_active ? 1 : 0.5,
                ringColor: primaryColor
              }}
            >
              {/* Drag Handle */}
              <GripVertical className="w-5 h-5 flex-shrink-0" style={{ color: textSecondary }} />
              
              {/* Thumbnail */}
              <div 
                className="w-24 h-14 rounded overflow-hidden flex-shrink-0"
                style={{ backgroundColor: `${borderColor}50` }}
              >
                <BannerThumbnail src={banner.image_url} alt={banner.alt_text || 'Banner'} />
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span 
                    className={`text-xs px-2 py-0.5 rounded ${banner.is_active ? '' : 'opacity-50'}`}
                    style={{ 
                      backgroundColor: banner.is_active ? `${primaryColor}20` : `${borderColor}50`,
                      color: banner.is_active ? primaryColor : textSecondary
                    }}
                  >
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {banner.start_date && (
                    <span className="text-xs" style={{ color: textSecondary }}>
                      From: {new Date(banner.start_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <a 
                  href={normalizeUrl(banner.link_url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm truncate block hover:underline"
                  style={{ color: textPrimary }}
                >
                  {banner.link_url}
                </a>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button 
                  onClick={() => handleToggleActive(banner)}
                  className="p-2 rounded-lg hover:opacity-70"
                  title={banner.is_active ? 'Deactivate' : 'Activate'}
                >
                  {banner.is_active ? (
                    <Eye className="w-4 h-4" style={{ color: primaryColor }} />
                  ) : (
                    <EyeOff className="w-4 h-4" style={{ color: textSecondary }} />
                  )}
                </button>
                <button 
                  onClick={() => handleEdit(banner)}
                  className="p-2 rounded-lg hover:opacity-70"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" style={{ color: textSecondary }} />
                </button>
                <a 
                  href={normalizeUrl(banner.link_url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:opacity-70"
                  title="Open link"
                >
                  <ExternalLink className="w-4 h-4" style={{ color: textSecondary }} />
                </a>
                <button 
                  onClick={() => handleDelete(banner.id)}
                  className="p-2 rounded-lg hover:opacity-70"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Help Text */}
      <p className="text-xs mt-4" style={{ color: textSecondary }}>
        Drag banners to reorder. Banners will rotate automatically in the carousel.
      </p>
    </div>
  );
};

export default B2BBannerManager;
